// src/context/AuthProvider.jsx
// Authentication context for the whole app.
// Holds the current user and exposes auth state and actions:
//   user        the logged-in user object, or null
//   isLoggedIn  true when a user is authenticated
//   isAdmin     true when the logged-in user has the admin role
//   login       authenticate with username and password
//   register    create an account (auto-logs in, since the API returns a token)
//   logout      clear the session
//
// The token and user are persisted in localStorage so a page reload restores the
// session synchronously, with no flash of the login page. The request interceptor
// in api/auth.js reads the token from the same localStorage key on every request.

import { createContext, useContext, useState } from "react";
import {
  login as apiLogin,
  createAccount as apiCreateAccount,
  TOKEN_KEY,
} from "../api/auth";

const USER_KEY = "user";

const AuthContext = createContext(null);

// Read any persisted user from localStorage. Runs once to initialize state.
// Wrapped in try/catch so a corrupted value cannot crash the app on load.
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  // Initialize synchronously from localStorage so the very first render already
  // knows whether someone is logged in.
  const [user, setUser] = useState(readStoredUser);

  // Persist a session (token + user) to localStorage and state.
  const applySession = ({ token, user: sessionUser }) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(sessionUser));
    setUser(sessionUser);
  };

  // Authenticate. Throws on failure so the caller can show the error.
  const login = async (credentials) => {
    const result = await apiLogin(credentials);
    applySession(result.data);
    return result.data.user;
  };

  // Register a new account. The API returns a token, so this logs the user in too.
  const register = async (data) => {
    const result = await apiCreateAccount(data);
    applySession(result.data);
    return result.data.user;
  };

  // Clear the session everywhere.
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const value = {
    user,
    isLoggedIn: Boolean(user),
    isAdmin: user?.role === "admin",
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook for consuming the auth context. Throws if used outside the provider,
// which catches wiring mistakes early.
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
